import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  PencilIcon,
  XMarkIcon,
  DocumentTextIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const DebitNoteDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const debitNoteId = id;
  const [debitNote, setDebitNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [downloadDropdown, setDownloadDropdown] = useState(false);
  const [printDropdown, setPrintDropdown] = useState(false);

  // Fetch debit note details
  const fetchDebitNote = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/finance/debit-notes/${debitNoteId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('debitNoteDetails.failedToFetchDebitNote'));
      }

      const data = await response.json();
      setDebitNote(data);
    } catch (error) {
      console.error('Error fetching debit note:', error);
      setError(t('debitNoteDetails.failedToLoadDebitNoteDetails'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debitNoteId) {
      fetchDebitNote();
    }
  }, [debitNoteId]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle download
  const handleDownload = async (debitNoteType = 'ORIGINAL') => {
    try {
      const response = await fetch(`/api/finance/debit-notes/${debitNoteId}/pdf?type=${debitNoteType}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('debitNoteDetails.failedToDownloadDebitNote'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debit-note-${debitNoteType.toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccessMessage(t('debitNoteDetails.downloadSuccess', { type: debitNoteType }));
      setDownloadDropdown(false);
    } catch (error) {
      console.error('Error downloading debit note:', error);
      setError(t('debitNoteDetails.failedToDownloadDebitNote'));
    }
  };

  // Handle print
  const handlePrint = async (debitNoteType = 'ORIGINAL') => {
    try {
      const response = await fetch(`/api/finance/debit-notes/${debitNoteId}/pdf?type=${debitNoteType}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('debitNoteDetails.failedToPrintDebitNote'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Open PDF in new tab for preview and printing
      const newWindow = window.open(url, '_blank');
      
      // Clean up URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);

      setSuccessMessage(t('debitNoteDetails.printSuccess', { type: debitNoteType }));
      setPrintDropdown(false);
    } catch (error) {
      console.error('Error printing debit note:', error);
      setError(t('debitNoteDetails.failedToPrintDebitNote'));
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'Draft': { bg: 'bg-gray-300', text: 'text-gray-800' },
      'Sent': { bg: 'bg-blue-100', text: 'text-blue-800' },
      'Paid': { bg: 'bg-green-100', text: 'text-green-800' },
      'Partial': { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      'Overdue': { bg: 'bg-red-100', text: 'text-red-800' },
      'Cancelled': { bg: 'bg-gray-100', text: 'text-gray-600' }
    };

    const config = statusConfig[status] || statusConfig['Draft'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {t(`debitNoteDetails.status${status}`)}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'BGN'
    }).format(amount);
  };

  // Format description with line breaks
  const formatDescription = (description) => {
    if (!description) return '';
    return description.split('\n').map((line, index) => (
      <span key={index}>
        {line}
        {index < description.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !debitNote) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error || t('debitNoteDetails.debitNoteNotFound')}</div>
        <button
          onClick={() => navigate('/admin/dashboard/invoices?tab=debit-notes')}
          className="text-blue-600 hover:text-blue-800"
        >
          {t('debitNoteDetails.backToDebitNotes')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage('')}
            className="text-green-600 hover:text-green-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard/invoices?tab=debit-notes')}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{debitNote.debitNoteNumber}</h1>
                <p className="text-gray-600">{t('debitNoteDetails.debitNoteDetails')}</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <div className="relative">
                <button 
                  onClick={() => setDownloadDropdown(!downloadDropdown)}
                  className="text-green-600 hover:text-green-900 p-2"
                  title={t('debitNoteDetails.downloadAsPDF')}
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                {downloadDropdown && (
                  <div 
                    className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleDownload('ORIGINAL');
                          setDownloadDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('debitNoteDetails.downloadOriginal')}
                      </button>
                      <button
                        onClick={() => {
                          handleDownload('COPY');
                          setDownloadDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('debitNoteDetails.downloadCopy')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                <button 
                  onClick={() => setPrintDropdown(!printDropdown)}
                  className="text-purple-600 hover:text-purple-900 p-2"
                  title={t('debitNoteDetails.printPDF')}
                >
                  <PrinterIcon className="h-5 w-5" />
                </button>
                {printDropdown && (
                  <div 
                    className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handlePrint('ORIGINAL');
                          setPrintDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('debitNoteDetails.printOriginal')}
                      </button>
                      <button
                        onClick={() => {
                          handlePrint('COPY');
                          setPrintDropdown(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('debitNoteDetails.printCopy')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button 
                onClick={() => onTabChange('debit-note-edit', { id: debitNote._id })}
                className="text-blue-600 hover:text-blue-900 p-2"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button 
                onClick={() => onTabChange('debit-notes')}
                className="text-red-600 hover:text-red-900 p-2"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
          {/* Debit Note Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteDetails.debitNoteInformation')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-sm text-gray-600">{t('debitNoteDetails.debitNoteNumber')}:</span>
                <p className="font-medium">{debitNote.debitNoteNumber}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('debitNoteDetails.originalInvoice')}:</span>
                <p className="font-medium">{debitNote.originalInvoiceNumber}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('debitNoteDetails.issueDate')}:</span>
                <p className="font-medium">{formatDate(debitNote.issueDate)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('debitNoteDetails.dueDate')}:</span>
                <p className="font-medium">{formatDate(debitNote.dueDate)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('debitNoteDetails.status')}:</span>
                <div className="mt-1">{getStatusBadge(debitNote.status)}</div>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('debitNoteDetails.currency')}:</span>
                <p className="font-medium">{debitNote.currency}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">{t('debitNoteDetails.type')}:</span>
                <p className="font-medium">{debitNote.invoiceType ? t(`debitNoteDetails.type${debitNote.invoiceType}`) : t('debitNoteDetails.typeORIGINAL')}</p>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteDetails.items')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('debitNoteDetails.no')}</th>
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('debitNoteDetails.description')}</th>
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('debitNoteDetails.quantity')}</th>
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('debitNoteDetails.unitPrice')}</th>
                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">{t('debitNoteDetails.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {debitNote.items.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 px-2 text-sm">{index + 1}.</td>
                      <td className="py-2 px-2 text-sm">
                        <div className="whitespace-pre-line">{formatDescription(item.description)}</div>
                      </td>
                      <td className="py-2 px-2 text-sm">{item.quantity}</td>
                      <td className="py-2 px-2 text-sm">{formatCurrency(item.unitPrice)}</td>
                      <td className="py-2 px-2 text-sm font-medium">{formatCurrency(item.quantity * item.unitPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteDetails.summary')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* BGN Totals */}
              <div className="space-y-3">
                <h3 className="text-md font-medium text-gray-900">BGN</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('debitNoteDetails.subtotal')}:</span>
                    <span className="font-medium">{formatCurrency(debitNote.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('debitNoteDetails.taxAmount')} (20%):</span>
                    <span className="font-medium">{formatCurrency(debitNote.taxAmount)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-medium">{t('debitNoteDetails.totalAmount')}:</span>
                    <span className="text-green-600 font-bold">{formatCurrency(debitNote.totalAmount)}</span>
                  </div>
                </div>
              </div>

              {/* EUR Totals */}
              <div className="space-y-3">
                <h3 className="text-md font-medium text-gray-900">EUR</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('debitNoteDetails.subtotal')}:</span>
                    <span className="font-medium">{formatCurrency(debitNote.subtotal / 1.95583)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('debitNoteDetails.taxAmount')} (20%):</span>
                    <span className="font-medium">{formatCurrency(debitNote.taxAmount / 1.95583)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-medium">{t('debitNoteDetails.totalAmount')}:</span>
                    <span className="text-green-600 font-bold">{formatCurrency(debitNote.totalAmount / 1.95583)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {debitNote.notes && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteDetails.notes')}</h2>
              <p className="text-gray-700 whitespace-pre-line">{debitNote.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Company Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteDetails.companySupplier')}</h2>
            <div className="space-y-2">
              <p className="font-medium">{debitNote.companyId?.name}</p>
              <p className="text-sm text-gray-600">{debitNote.companyId?.address}</p>
              <p className="text-sm text-gray-600">{t('debitNoteDetails.mol')}: {debitNote.companyId?.materiallyResponsiblePerson?.name}</p>
              {debitNote.companyId?.vatTaxId && debitNote.companyId.vatTaxId.trim() !== '' ? (
                <p className="text-sm text-gray-600">{t('debitNoteDetails.vatTaxId')}: BG{debitNote.companyId.vatTaxId}</p>
              ) : (
                <p className="text-sm text-gray-600">{t('debitNoteDetails.taxId')}: {debitNote.companyId?.taxId}</p>
              )}
            </div>
          </div>

          {/* Partner Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteDetails.partnerRecipient')}</h2>
            <div className="space-y-2">
              <p className="font-medium">{debitNote.partnerId?.name}</p>
              <p className="text-sm text-gray-600">{debitNote.partnerId?.address}</p>
              <p className="text-sm text-gray-600">{t('debitNoteDetails.contact')}: {debitNote.partnerId?.contactPerson}</p>
              {debitNote.partnerId?.vatTaxId && debitNote.partnerId.vatTaxId.trim() !== '' ? (
                <p className="text-sm text-gray-600">{t('debitNoteDetails.vatTaxId')}: BG{debitNote.partnerId.vatTaxId}</p>
              ) : (
                <p className="text-sm text-gray-600">{t('debitNoteDetails.taxId')}: {debitNote.partnerId?.taxId}</p>
              )}
            </div>
          </div>

          {/* Bank Account Information */}
          {debitNote.selectedBankAccount && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteDetails.bankAccount')}</h2>
              <div className="space-y-2">
                <p className="text-sm text-gray-600">{t('debitNoteDetails.bank')}: {debitNote.selectedBankAccount.bankName}</p>
                <p className="text-sm text-gray-600">{t('debitNoteDetails.bic')}: {debitNote.selectedBankAccount.swiftCode}</p>
                <p className="text-sm text-gray-600">{t('debitNoteDetails.iban')}: {debitNote.selectedBankAccount.iban}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default DebitNoteDetails; 